<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <div>
          <h2 class="modal-title">{{ isEditMode ? 'Edit Account' : 'Add Account' }}</h2>
          <p class="modal-desc" v-if="!isEditMode && parentAccount">Under: <strong>{{ parentAccount.code }} - {{ parentAccount.name }}</strong></p>
          <p class="modal-desc" v-if="isEditMode && editAccount">Editing: <strong>{{ editAccount.code }} - {{ editAccount.name }}</strong></p>
        </div>
        <button class="close-button" @click="$emit('close')">&times;</button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label class="form-label">Account Name <span class="required">*</span></label>
            <input v-model="formData.name" type="text" class="form-input" placeholder="e.g., Cash In Hand" required :disabled="submitting" />
          </div>

          <div class="form-group">
            <label class="form-label">Description</label>
            <input v-model="formData.description" type="text" class="form-input" placeholder="Optional description" :disabled="submitting" />
          </div>

          <div class="form-group">
            <label class="form-label">Account Nature</label>
            <div class="toggle-group">
              <button type="button" :class="['toggle-btn', { active: !formData.isTransactionAccount }]" @click="formData.isTransactionAccount = false" :disabled="submitting">
                Category (Group)
              </button>
              <button type="button" :class="['toggle-btn', { active: formData.isTransactionAccount }]" @click="formData.isTransactionAccount = true" :disabled="submitting">
                Transaction Account
              </button>
            </div>
          </div>
        </form>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')" :disabled="submitting">Cancel</button>
        <button class="btn-submit" @click="handleSubmit" :disabled="submitting || !formData.name">
          {{ submitLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'AddChartOfAccountModal',
  props: {
    isOpen: { type: Boolean, required: true },
    parentAccount: { type: Object, default: null },
    editAccount: { type: Object, default: null }
  },
  data () {
    return {
      formData: {
        name: '',
        description: '',
        isTransactionAccount: false
      },
      submitting: false
    }
  },
  computed: {
    isEditMode () {
      return !!this.editAccount
    },
    submitLabel () {
      if (this.submitting) return this.isEditMode ? 'Updating...' : 'Adding...'
      return this.isEditMode ? 'Update Account' : 'Add Account'
    }
  },
  watch: {
    isOpen (val) {
      if (val) {
        if (this.editAccount) {
          this.formData = {
            name: this.editAccount.name || '',
            description: this.editAccount.description || '',
            isTransactionAccount: this.editAccount.type === 'account'
          }
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    ...mapActions('accounting', ['addChartOfAccount', 'updateChartOfAccount']),

    async handleSubmit () {
      if (!this.formData.name) return

      this.submitting = true
      try {
        if (this.isEditMode) {
          await this.updateChartOfAccount({
            id: this.editAccount.id,
            payload: {
              name: this.formData.name,
              description: this.formData.description || '',
              parentID: this.editAccount.parentID || null,
              isTransactionAccount: this.formData.isTransactionAccount ? 1 : 0
            }
          })
          this.$emit('updated')
        } else {
          await this.addChartOfAccount({
            name: this.formData.name,
            description: this.formData.description || '',
            parentID: this.parentAccount ? this.parentAccount.id : null,
            isTransactionAccount: this.formData.isTransactionAccount ? 1 : 0
          })
          this.$emit('added')
        }
        this.$emit('close')
      } catch (e) {
        // error handled in store
      } finally {
        this.submitting = false
      }
    },

    resetForm () {
      this.formData = {
        name: '',
        description: '',
        isTransactionAccount: false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 480px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.modal-desc {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.close-button:hover { background: #f3f4f6; color: #1f2937; }

.modal-body { padding: 24px; }

.form-group { margin-bottom: 20px; }

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.required { color: #ef4444; }

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-input:disabled { background: #f9fafb; cursor: not-allowed; }
.form-input::placeholder { color: #9ca3af; }

.toggle-group {
  display: flex;
  gap: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.toggle-btn {
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: white;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn + .toggle-btn { border-left: 1px solid #e5e7eb; }
.toggle-btn.active { background: #2563eb; color: white; }
.toggle-btn:disabled { cursor: not-allowed; opacity: 0.7; }

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
}

.btn-cancel {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel:hover:not(:disabled) { background: #f3f4f6; }

.btn-submit {
  padding: 10px 24px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-submit:hover:not(:disabled) { background: #1d4ed8; }
.btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
</style>
